<template>
  <div>
    <modal v-if="showModal" @close="close">
      <template slot="body" v-if="propIsLoading">
        <Loader bg-color="#f6f9fc"></Loader>
      </template>
      <template slot="body" v-else>
        <div class="add-lesson__header" :style="{'backgroundColor': color ? color : '#fff'}">
          <h2>{{ name.length > 0 ? name : !isActivities ? i18n[locale]['create_subject'] : 'Opret aktivitet' }}</h2>
          <div class="add-lesson__calendars">
            {{ showMasterCalendarName }}
          </div>
          <div class="add-lesson__header__task-overview"
               v-if="activeTab === 'edit' && !isActivities && canSeeTaskOverviewToggle"
               :content="i18n[locale]['show_on_task_overview_desc']"
               v-tippy="{
                 arrow: false,
                 animateFill: false,
                 animation: 'shift-away',
                 zIndex: '999999992',
                 boundary: 'window'}"
               @click.stop="toggleTaskOverview">
            <label>{{ i18n[locale]['show_on_task_overview'] }}</label>
            <AppToggle :propOn="taskOverview" :allowChanges="false"></AppToggle>
          </div>
          <div class="add-lesson__header__tabs" v-if="editSubject && !isActivities">
            <div class="add-lesson__header__tabs__tab" :class="{'tab--is-active': activeTab === 'edit'}"
                 @click="activeTab = 'edit'">
              {{ i18n[locale].edit_subject }}
            </div>
            <div class="add-lesson__header__tabs__tab" :class="{'tab--is-active': activeTab === 'history'}"
                 @click="activeTab = 'history'">
              {{ i18n[locale].activity }}
            </div>
            <div
              class="add-lesson__header__tabs__tab"
              v-if="isShowSubjectsPools"
              :class="{ 'tab--is-active': activeTab === 'calendarHours' }"
              @click="onClickCalendarHoursTab"
            >
              Puljer
            </div>
            <div class="add-lesson__header__tabs__tab"
                 v-if="isReductionsActive && isShowReductions"
                 :class="{'tab--is-active': activeTab === 'reductions'}"
                 @click="activeTab = 'reductions'">
              Reduktioner
            </div>
          </div>
        </div>
        <div class="add-lesson__form">
          <div class="add-lesson__form__content" v-if="activeTab === 'history'">
            <h2>{{ i18n[locale].latest_activity }}</h2>
            <SubjectFormLog :subject="editSubject"></SubjectFormLog>
          </div>
          <div class="add-lesson__form__content" v-if="activeTab === 'reductions'">
            <h2>Reduktioner</h2>
            <SubjectFormReductions :subject="editSubject"></SubjectFormReductions>
          </div>
          <div class="add-lesson__form__content" v-if="activeTab === 'calendarHours'">
            <SubjectFormCalendarHours :subject="editSubject" @subjectHoursSaved="onSubjectHoursSaved"></SubjectFormCalendarHours>
          </div>
          <div class="add-lesson__form__content" v-if="activeTab === 'edit'">
            <template v-if="editSubject">
              <div class="subjects-requested-by do-warning" v-if="isShowSubjectsRequest && subjectRequestedBy">
                <label class="do-label">{{ i18n[locale]['requested_by'] }}</label> {{ subjectRequestedBy }}
              </div>
              <template v-if="editSubject && showDanishPreparationFactorBesideHours && !isDanishPrepBinaryOnOwnRow">
                <div class="input-group input-group__flex">
                  <div class="flex-">
                    <label>{{ hoursLabel }}</label>
                    <div :content="i18n[locale]['hours_disabled_desc']" v-tippy="hoursDisabledTippy">
                      <input type="number" ref="inputLessonCount" v-model.number="hours"
                             :disabled="isHoursDisabled"
                             @focus="selectAllOnFocus($event)"
                             class="do-input"
                             :class="{'input--error': formErrors['hours'], 'input--disabled': isHoursDisabled}">
                    </div>
                    <p class="input-error" v-if="formErrors['hours']">{{ formErrors['hours'][locale] }}</p>
                  </div>
                  <DkPreparationFactorControl
                    v-model="preparation"
                    :subject-type="subjectType"
                    :school-prep-factor="schoolSettings.prepFactor"
                    :labels="danishPreparationControlLabels"
                    wrap-class="flex-1">
                  </DkPreparationFactorControl>
                </div>
              </template>
              <template v-else-if="editSubject && showDanishPreparationFactorBesideHours && isDanishPrepBinaryOnOwnRow">
                <div class="input-group">
                  <label>{{ hoursLabel }}</label>
                  <div :content="i18n[locale]['hours_disabled_desc']" v-tippy="hoursDisabledTippy">
                    <input type="number" ref="inputLessonCount" v-model.number="hours"
                           :disabled="isHoursDisabled"
                           @focus="selectAllOnFocus($event)"
                           class="do-input"
                           :class="{'input--error': formErrors['hours'], 'input--disabled': isHoursDisabled}">
                  </div>
                  <p class="input-error" v-if="formErrors['hours']">{{ formErrors['hours'][locale] }}</p>
                </div>
                <div class="input-group">
                  <DkPreparationFactorControl
                    v-model="preparation"
                    :subject-type="subjectType"
                    :school-prep-factor="schoolSettings.prepFactor"
                    :labels="danishPreparationControlLabels"
                    wrap-class="input-group dk-prep-factor-full-width">
                  </DkPreparationFactorControl>
                </div>
              </template>
              <div class="input-group" v-else-if="editSubject">
                <label>{{ hoursLabel }}</label>
                <div :content="i18n[locale]['hours_disabled_desc']" v-tippy="hoursDisabledTippy">
                  <input type="number" ref="inputLessonCount" v-model.number="hours"
                         :disabled="isHoursDisabled"
                         @focus="selectAllOnFocus($event)"
                         class="do-input"
                         :class="{'input--error': formErrors['hours'], 'input--disabled': isHoursDisabled}">
                </div>
                <p class="input-error" v-if="formErrors['hours']">{{ formErrors['hours'][locale] }}</p>
              </div>
              <div class="input-group" v-if="calendarType !== 'user'">
                <label><span class="icon-user"></span> {{ i18n[locale]['add_teacher'] }}</label>
                <DSelectCalendarIds itemType="USER"
                                    :showTags="true"
                                    :preSelectedItems="userCalendarIds"
                                    :sortSelectedByName="true"
                                    @update="setSelectedUsers">
                </DSelectCalendarIds>
              </div>
              <div class="input-group">
                <label><span class="icon-location"></span> {{ i18n[locale]['add_room'] }}</label>
                <DSelectCalendarIds itemType="ROOM"
                                    :preSelectedItems="roomCalendarIds"
                                    @update="setSelectedRooms">
                </DSelectCalendarIds>
              </div>
              <p class="text-link" @click="isPossibleToAddKlasses = true"
                 v-if="!isPossibleToAddKlasses && isAllowedToAddKlasses">{{ i18n[locale]['add_klass'] }}?</p>
              <transition name="dropdown">
                <div class="input-group" v-if="isPossibleToAddKlasses">
                  <label><span class="icon-calendar4"></span> {{ i18n[locale]['add_klass'] }}</label>
                  <DSelectCalendarIds itemType="klass"
                                      :calendarId="calendarId"
                                      :showTags="true"
                                      :preSelectedItems="klassCalendarIds"
                                      :showCreateButton="false"
                                      @update="setSelectedKlass">
                  </DSelectCalendarIds>
                </div>
              </transition>
              <div class="input-group">
                <div class="date-toggle">
                  <div class="date-toggle__inner" @click.stop="toggleDateFields">
                    <AppToggle :propOn="isShowDateFields" :allowChanges="false"></AppToggle>
                    <label style="margin-bottom: 0">{{ i18n[locale]['show_dates_on_overview'] }}</label>
                  </div>
                </div>
              </div>
              <transition name="dropdown">
                <div class="input-group__flex" v-if="isShowDateFields">
                  <div class="input-group">
                    <label>{{ i18n[locale]['from_date'] }}</label>
                    <div class="date-field">
                      <DateSelector
                        :date="fromDate || today"
                        :width100="true"
                        @changed="setFromDate">
                      </DateSelector>
                    </div>
                  </div>
                  <div class="input-group">
                    <label>{{ i18n[locale]['to_date'] }}</label>
                    <div class="date-field">
                      <DateSelector
                        :date="toDate || today"
                        :width100="true"
                        @changed="setToDate">
                      </DateSelector>
                    </div>
                  </div>
                </div>
              </transition>
              <div class="input-group">
                <p class="text-blue advanced-settings__link" @click="isEditSubjectAdvanced = !isEditSubjectAdvanced">
                  {{ i18n[locale]['more_settings'] }}
                </p>
              </div>
            </template>
            <template v-if="!editSubject || isEditSubjectAdvanced">
              <div class="input-group__flex">
                <div class="input-group">
                  <label>{{ i18n[locale]['subject_title'] }}</label>
                  <input type="text" v-model="name" :placeholder="isActivities ? 'Eks. Motionsdag' : i18n[locale]['ex_subject']"
                         @keyup="removeErrors('name')"
                         ref="name"
                         class="do-input"
                         :class="{'input--error': formErrors['name']}">
                  <p class="input-error" v-if="formErrors['name']">{{ formErrors['name'][locale] }}</p>
                </div>
                <div class="input-group" style="max-width: 100px" v-if="isShowNorwegianPreparationDropdown">
                  <label>Årsramme</label>
                  <select v-model.number="preparation" class="d-input__select">
                    <option v-for="prep in preparationOptionsForNorway" :key="prep">
                      {{ prep }}
                    </option>
                  </select>
                </div>
                <div class="input-group" style="max-width: 160px" v-else-if="showDanishPreparationFactorInTitleRow && !isDanishPrepBinaryOnOwnRow">
                  <DkPreparationFactorControl
                    v-model="preparation"
                    :subject-type="subjectType"
                    :school-prep-factor="schoolSettings.prepFactor"
                    :labels="danishPreparationControlLabels">
                  </DkPreparationFactorControl>
                </div>
                <div class="input-group input-group__subject-codes" style="max-width: 100px">
                  <label>{{ i18n[locale]['subject_code'] }}</label>
                <select v-if="isSchoolFromDenmark"
                        v-model.number="subjectCode"
                        class="d-input__select">
                  <option :value="''"></option>
                  <option v-for="subject in standardSubjectCodes"
                          :key="subject.subject_code"
                          :value="subject.subject_code">
                    {{ subject.fagkode }} - {{ subject.name }}
                  </option>
                </select>
                <input v-else
                       type="text"
                       class="do-input"
                       v-model.number="subjectCode"
                       @focus="selectAllOnFocus($event)">
                </div>
              </div>
              <div class="input-group" v-if="showDanishPreparationFactorInTitleRow && isDanishPrepBinaryOnOwnRow">
                <DkPreparationFactorControl
                  v-model="preparation"
                  :subject-type="subjectType"
                  :school-prep-factor="schoolSettings.prepFactor"
                  :labels="danishPreparationControlLabels"
                  wrap-class="input-group dk-prep-factor-full-width">
                </DkPreparationFactorControl>
              </div>
              <div class="input-group input-group__flex" v-if="!editSubject && !isEditSubjectAdvanced">
                <div class="flex-">
                  <label>{{ i18n[locale]['hours_in_school_year'] }}</label>
                  <div :content="i18n[locale]['hours_disabled_desc']" v-tippy="hoursDisabledTippy">
                    <input type="number" ref="inputLessonCount" v-model.number="hours"
                           :disabled="isHoursDisabled"
                           class="do-input"
                           @focus="selectAllOnFocus($event)"
                           @blur="removeErrors('hours')"
                           :class="{'input--error': formErrors['hours'], 'input--disabled': isHoursDisabled}">
                  </div>
                  <p class="input-error" v-if="formErrors['hours']">{{ formErrors['hours'][locale] }}</p>
                </div>
                <DkPreparationFactorControl
                  v-if="showDanishPreparationFactorBesideHours && !isDanishPrepBinaryOnOwnRow"
                  v-model="preparation"
                  :subject-type="subjectType"
                  :school-prep-factor="schoolSettings.prepFactor"
                  :labels="danishPreparationControlLabels"
                  wrap-class="flex-1">
                </DkPreparationFactorControl>
                <div class="flex-1">
                  <label>{{ i18n[locale]['standard_duration'] }}</label>
                  <div class="input-group--flex">
                    <div class="flex-1">
                      <input type="number"
                             class="do-input"
                             v-model="defaultDuration"
                             ref="input_default_duration"
                             @focus="selectAllOnFocus($event)">
                      <p class="input-error"
                         v-if="formErrors['defaultDuration']">{{ formErrors['defaultDuration'][locale] }}</p>
                    </div>
                    <div class="input-group--flex__setting-icon"
                         v-if="userCanManageSchool"
                         :content="$t('vue2.set_standard_duration.set_standard_duration_for_the_school')"
                         v-tippy="{
            arrow: true
          }">
                      <div class="icon__bg-circle" @click="isOpenDurationModal = true"><span class="icon-wrench"></span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <div class="input-group" v-if="!editSubject && !isEditSubjectAdvanced && showDanishPreparationFactorBesideHours && isDanishPrepBinaryOnOwnRow">
                <DkPreparationFactorControl
                  v-model="preparation"
                  :subject-type="subjectType"
                  :school-prep-factor="schoolSettings.prepFactor"
                  :labels="danishPreparationControlLabels"
                  wrap-class="input-group dk-prep-factor-full-width">
                </DkPreparationFactorControl>
              </div>
              <div class="input-group" v-if="!calendarId">
                <label>{{ i18n[locale]['choose_class'] }}</label>
                <DSelectKlass @add="addKlass" @remove="removeKlass"></DSelectKlass>
                <p class="input-error" v-if="formErrors['calendarId']">{{ formErrors['calendarId'][locale] }}</p>
              </div>
              <div class="input-group">
                <label>{{ i18n[locale]['choose_color'] }}</label>
                <ColorPicker @changedColor="setSubjectColor"
                             :initialColor="color">
                </ColorPicker>
              </div>
              <div class="input-group" v-if="!isActivities">
                <label>{{ i18n[locale]['choose_subject_type'] }}</label>
                <SubjectTypePicker
                  :calendarType="calendarType"
                  :subjectType="subjectType"
                  :showActivities="showActivities"
                  @change="selectSubjectType">
                </SubjectTypePicker>
              </div>
            </template>
            <div class="edit-subject add-lesson__footer" v-if="editSubject">
              <div class="input-group input-group__submit">
                <div class="input-group__submit__left">
                  <button v-if="editSubjectCanBeDeleted"
                          class="hint--right bttn--warning"
                          :aria-label="i18n[locale]['delete_subject']"
                          @click="deleteEditSubject">
                    <span class="icon-bin"></span>
                  </button>
                  <div class="hint--right" v-else :aria-label="i18n[locale]['cannot_delete']">
                    <button class="bttn bttn--disabled">
                      <span class="icon-bin"></span>
                    </button>
                  </div>
                </div>
                <div class="input-group__submit__right">
                  <button class="do-bttn" @click="close">{{ i18n[locale]['cancel'] }}</button>
                  <button class="do-bttn do-bttn--submit" v-if="editSubject"
                          @click="updateSubject">{{ i18n[locale]['update_subject'] }}
                  </button>
                </div>
              </div>
            </div>
            <div class="input-group input-group__submit add-lesson__footer" v-else>
              <div class="input-group__submit__left">
              </div>
              <div class="input-group__submit__right">
                <button class="do-bttn" @click="close">{{ i18n[locale]['cancel'] }}</button>
                <button class="do-bttn do-bttn--submit" @click="createSubject">
                  {{ !isActivities ? i18n[locale]['create_subject'] : 'Opret aktivitet' }}
                </button>
              </div>
            </div>
          </div>
        </div>
      </template>
    </modal>
    <StandardDurationSetting
      :isOpenModal="isOpenDurationModal"
      @close="isOpenDurationModal = false"
      @submit="saveUpdatedDuration">
    </StandardDurationSetting>
    <SubjectFormDeleteSubjects
      v-if="subjectsWithSameName.length > 0"
      :subjectsWithSameName="subjectsWithSameName"
      @close="closeModalSubjectsWithSameName"
      @keep="keepExistingSubjects"
      @delete="deleteExistingSubjects">
    </SubjectFormDeleteSubjects>
    <SubjectPoolsUpdateConfirm
      v-if="showPoolsConfirm"
      :subject="editSubject"
      :newHours="hours"
      @updateAll="onPoolsConfirmUpdateAll"
      @subjectOnly="onPoolsConfirmSubjectOnly"
      @close="onPoolsConfirmClose">
    </SubjectPoolsUpdateConfirm>
    <SubjectAssignTeacherToEventsConfirm
      v-if="showAssignTeacherToEventsConfirm"
      :teacherNames="assignTeacherToEventsNames"
      :initialDate="assignTeacherToEventsInitialDate"
      :mode="assignTeacherToEventsAction"
      :calendarKind="assignTeacherToEventsKind"
      :color="color"
      :subjectName="name"
      @confirm="onAssignTeacherToEventsConfirm"
      @skip="onAssignTeacherToEventsSkip">
    </SubjectAssignTeacherToEventsConfirm>
  </div>
</template>

<script>
import modal from './Modal'
import DSelectUser from '../DSelect/DSelectUsers'
import DSelectKlass from '../DSelect/DSelectKlass'
import ColorPicker from './ColorPicker'
import SubjectTypePicker from './SubjectFormSubjectType'
import DSelectCalendarIds from '../DSelect/DSelectCalsIds'
import SubjectFormLog from './SubjectFormLog'
import StandardDurationSetting from '../School/StandardDurationSetting'
import SubjectFormDeleteSubjects from './SubjectFormDeleteSubjects'
import SubjectFormReductions from './SubjectFormReductions.vue'
import DateSelector from '../datepicker/DateSelector'
import AppToggle from '../common/AppToggle'
import moment from 'moment'
import Loader from '../common/Loader.vue'
import SubjectFormCalendarHours from './SubjectFormCalendarHours.vue'
import DkPreparationFactorControl from './DkPreparationFactorControl.vue'
import SubjectPoolsUpdateConfirm from './SubjectPoolsUpdateConfirm.vue'
import SubjectAssignTeacherToEventsConfirm from './SubjectAssignTeacherToEventsConfirm.vue'
import assignTeacherToScheduledEvents from '../../mixins/assignTeacherToScheduledEvents'
import {shouldConfirmPoolsUpdate} from '../../utils/subjectPools'
import docendo from '../../docendo'
import eventbus from '../../eventbus'
import {
  isSubjectTypeWithDanishPreparationFactor,
  isDanishPreparationBinaryPrepTaskSubjectType,
  schoolHasDanishPreparationFactorConfigured,
  preparationPayloadForSave,
  preparationFromApiForForm
} from '../../utils/dkPreparationFactorUi'

const I18N = {
  da: {
    create_subject: 'Opret fag',
    hours_in_school_year: 'Klokketimer i skoleåret',
    add_teacher: 'Tilføj underviser',
    add_room: 'Tilføj lokale',
    add_klass: 'Tilføj klasse',
    more_settings: 'Yderligere indstillinger',
    subject_title: 'Fagets navn',
    standard_duration: 'Normal varighed (i minutter)',
    choose_class: 'Vælg klasse',
    choose_color: 'Vælg farve',
    choose_subject_type: 'Vælg briktype',
    ex_subject: 'Eks. Dansk',
    cancel: 'Annullér',
    update_subject: 'Opdatér',
    delete_subject: 'Slet faget i klassen',
    edit_subject: 'Redigér',
    activity: 'Historik',
    latest_activity: 'Seneste aktivitet',
    cannot_delete: 'Ikke muligt at slette, da brikken er skemalagt',
    requested_by: 'Ønsket af',
    subject_code: 'Fagkode',
    forb_faktor: 'Forb. faktor',
    forb_faktor_range_hint: 'Mellem 0 og -1',
    prep_task_extra_toggle: 'Medregnes ud over forberedelse fra forberedelesfaktor',
    prep_task_extra_toggle_hint: 'Fra: opgaven følger skolens forberedelsesfaktor som øvrige brikker. Til: opgavens timer medregnes med faktor 1 oven i forberedelsen fra undervisningsfag.',
    from_date: 'Startdato',
    to_date: 'Slutdato',
    show_dates_on_overview: 'Vis datoer på opgaveoversigten',
    show_on_task_overview: 'Vis på opgaveoversigt',
    show_on_task_overview_desc: 'Når denne er slået fra, vises faget ikke på opgaveoversigten.',
    hours_disabled_desc: 'Klokketimer kan kun angives, når "Vis på opgaveoversigt" er slået til, da timerne bruges på opgaveoversigten.',
    pool_for: 'pulje for',
    pool_save_failed: 'Kunne ikke opdatere puljen'
  },
  en: {
    create_subject: 'Create subject',
    hours_in_school_year: 'Hours in school year',
    add_teacher: 'Add teacher',
    add_klass: 'Add grade/group',
    add_room: 'Add room',
    more_settings: 'Advanced settings',
    subject_title: 'Subject title',
    standard_duration: 'Standard duration (in minutes)',
    choose_class: 'Choose class',
    choose_color: 'Choose color',
    choose_subject_type: 'Choose subject category',
    ex_subject: 'E.g. Math',
    cancel: 'Cancel',
    update_subject: 'Update subject',
    delete_subject: 'Delete subject in class',
    edit_subject: 'Edit subject',
    activity: 'History',
    latest_activity: 'Activity',
    cannot_delete: 'Not possible to delete because the subject has lessons in a calendar',
    requested_by: 'Requested by',
    subject_code: 'Subject code',
    forb_faktor: 'Prep factor',
    forb_faktor_range_hint: 'Between 0 and -1',
    prep_task_extra_toggle: 'Counted in addition to preparation from teaching',
    prep_task_extra_toggle_hint: 'Off: this task uses the school preparation factor like other tiles. On: hours are counted with factor 1 on top of preparation from teaching subjects.',
    from_date: 'Start date',
    to_date: 'End date',
    show_dates_on_overview: 'Show dates on task overview',
    show_on_task_overview: 'Show on task overview',
    show_on_task_overview_desc: 'When turned off, the subject is not shown on the task overview.',
    hours_disabled_desc: 'Hours can only be entered when "Show on task overview" is turned on, since the hours are used on the task overview.',
    pool_for: 'pool for',
    pool_save_failed: 'Could not update the pool'
  },
  nb: {
    create_subject: 'Opprett fag',
    hours_in_school_year: 'Årstimetall',
    add_teacher: 'Legg til lærer',
    add_room: 'Legg til lokale',
    add_klass: 'Legg til klasse',
    more_settings: 'Flere innstillinger',
    subject_title: 'Navn på fag',
    standard_duration: 'Normal varighet (i minutter)',
    choose_class: 'Velg klasse',
    choose_color: 'Velg farge',
    choose_subject_type: 'Velg type',
    ex_subject: 'F.eks. Norsk',
    cancel: 'Angre',
    update_subject: 'Oppdater fag',
    delete_subject: 'Slett fag',
    edit_subject: 'Rediger fag',
    activity: 'Historikk',
    latest_activity: 'Aktivitet',
    cannot_delete: 'Ikke mulig å slette fordi faget har leksjoner i en kalender',
    requested_by: 'Ønsket av',
    subject_code: 'Fagkode',
    forb_faktor: 'Forb. faktor',
    forb_faktor_range_hint: 'Mellom 0 og -1',
    prep_task_extra_toggle: 'Tas med i tillegg til forberedelse fra undervisning',
    prep_task_extra_toggle_hint: 'Av: oppgaven følger skolens forberedelsesfaktor som andre brikker. På: oppgavens timer tas med med faktor 1 i tillegg til forberedelse fra undervisningsfag.',
    from_date: 'Startdato',
    to_date: 'Sluttdato',
    show_dates_on_overview: 'Vis dato på oppgaveoversikt',
    show_on_task_overview: 'Vis på oppgaveoversikt',
    show_on_task_overview_desc: 'Når denne er slått av, vises faget ikke på oppgaveoversikten.',
    hours_disabled_desc: 'Årstimetall kan kun angis når "Vis på oppgaveoversikt" er slått på, da timene brukes på oppgaveoversikten.',
    pool_for: 'pulje for',
    pool_save_failed: 'Kunne ikke oppdatere puljen'
  }
}

export default {
  mixins: [assignTeacherToScheduledEvents],
  components: {
    SubjectAssignTeacherToEventsConfirm,
    SubjectPoolsUpdateConfirm,
    SubjectFormCalendarHours,
    Loader,
    SubjectFormReductions,
    StandardDurationSetting,
    modal,
    ColorPicker,
    DSelectCalendarIds,
    DSelectUser,
    DSelectKlass,
    SubjectTypePicker,
    SubjectFormLog,
    SubjectFormDeleteSubjects,
    DkPreparationFactorControl,
    DateSelector,
    AppToggle
  },
  props: {
    showModal: {
      default: false
    },
    editSubject: {
      default: null
    },
    isShowSubjectsRequest: {
      type: Boolean,
      default: false
    },
    subjectsRequestsInHash: {
      type: Object,
      default: () => {
      }
    },
    isActivities: {
      type: Boolean,
      default: false
    },
    propCalendarId: {
      default: null
    },
    poolCalendarId: {
      default: null
    },
    propTab: {
      default: null
    },
    isShowReductions: {
      default: false
    },
    propIsLoading: {
      default: false
    },
    allowDanishPreparationFactorUi: {
      type: Boolean,
      default: false
    },
    showActivities: {
      type: Boolean,
      default: false
    },
    isInDCalendar: {
      type: Boolean,
      default: false
    }
  },
  data () {
    return {
      preparationOptionsForNorway: [741, 711, 664, 635, 606],
      preparation: null,
      name: '',
      color: '#A0A5EF',
      calendarId: null,
      defaultDuration: 45,
      hours: 0,
      subjectType: null,
      klassCalendarIds: [],
      userCalendarIds: [],
      roomCalendarIds: [],
      formErrors: [],
      isEditSubjectAdvanced: false,
      activeTab: 'edit', // edit, history, reductions
      subjectCode: '',
      isOpenDurationModal: false,
      isPossibleToAddKlasses: false,
      subjectsWithSameName: [],
      updatedSubject: null,
      fromDate: null,
      toDate: null,
      isShowDateFields: false,
      taskOverview: false,
      showPoolsConfirm: false,
      savedSubjectHours: null
    }
  },
  computed: {
    locale () {
      return this.$store.getters.locale
    },
    subjectHasDifferentiatedPools () {
      if (!this.editSubject) return false
      const subj = this.editSubject
      if (!subj) return false

      let rows = []
      if (subj.subject_calendars && subj.subject_calendars.length) {
        rows = subj.subject_calendars
      } else if (subj.calendars_subjects && subj.calendars_subjects.length) {
        rows = subj.calendars_subjects
      } else {
        return false
      }

      for (let i = 0; i < rows.length; i++) {
        const sc = rows[i]
        if (!sc) continue
        // Primært felt: hours. Fallback: value (hvis din backend skulle kalde det sådan enkelte steder)
        const v = sc.hours !== undefined ? sc.hours : sc.value
        if (v !== null && v !== undefined) return true
      }
      return false
    },
    subjectRequestedBy () {
      if (!this.isShowSubjectsRequest || !this.editSubject) {
        return []
      }
      const calendarIds = this.subjectsRequestsInHash[this.editSubject.id] || []
      return calendarIds.filter(calId => this.allCalendarsInHash[calId]).map(calId => this.allCalendarsInHash[calId]['name']).join(' ,')
    },
    isArbeidstidsplanActivated () {
      return this.$store.getters.isArbeidstidsplanActivated
    },
    schoolCountry () {
      return this.$store.getters.schoolCountry
    },
    isSchoolFromDenmark () {
      return this.schoolCountry.toLowerCase() === 'dk'
    },
    standardSubjectCodes () {
      return this.$store.getters.standardSubjectCodes || []
    },
    isReductionsActive () {
      return this.$store.getters.isReductionsActive
    },
    i18n () {
      return I18N
    },
    isAllowedToAddKlasses () {
      return this.calendarType === 'klass' || this.calendarType === 'user'
    },
    user () {
      return this.$store.state.user
    },
    isPermissionsActivatedForSchool () {
      return this.$store.getters.isPermissionsActivatedForSchool
    },
    userPermissions () {
      return this.$store.getters.userPermissions
    },
    userCanManageSchool () {
      return this.userHasPermission('can_manage_school')
    },
    canChangeTaskOverview () {
      return this.userHasPermission('can_change_task_overview')
    },
    canSeeTaskOverviewToggle () {
      return this.canChangeTaskOverview
    },
    isHoursDisabled () {
      return !this.isActivities && !this.taskOverview
    },
    hoursDisabledTippy () {
      return {
        arrow: false,
        animateFill: false,
        animation: 'shift-away',
        zIndex: '999999992',
        boundary: 'window',
        onShow: () => this.isHoursDisabled
      }
    },
    activeSchool () {
      return this.$store.state.activeSchool
    },
    editSubjectCanBeDeleted () {
      return this.editSubject && this.editSubject.can_delete
    },
    calendarType () {
      if (!this.calendarId) {
        return 'klass'
      }
      const cal = this.allCalendarsInHash[this.calendarId]
      if (cal) {
        return cal.type
      }
      // Kalenderen er ikke nødvendigvis indlæst i store (fx ejerens brugerkalender i Timeopgørelse)
      const subjectCal = this.editSubject && Array.isArray(this.editSubject.cals)
        ? this.editSubject.cals.find(c => c && (c.id * 1) === (this.calendarId * 1))
        : null
      return subjectCal && subjectCal.calendar_type ? subjectCal.calendar_type : 'klass'
    },
    allCalendarsInHash () {
      return this.$store.getters.allCalendarsInHash
    },
    klassCalendarInHash () {
      return this.$store.getters.allKlassCalendarsInHash
    },
    subjectsInKlassHash () {
      return this.$store.getters.subjectsInKlassHash
    },
    subjectsInHash () {
      return this.$store.getters.subjectsInHash
    },
    poolEntry () {
      if (!this.poolCalendarId || !this.editSubject || !Array.isArray(this.editSubject.subject_calendars)) {
        return null
      }
      return this.editSubject.subject_calendars.find(sc =>
        sc && (sc.calendar_id * 1) === (this.poolCalendarId * 1) && sc.hours !== null && sc.hours !== undefined
      ) || null
    },
    isEditingPool () {
      return !!this.poolEntry
    },
    poolCalendarName () {
      const cal = this.allCalendarsInHash[this.poolCalendarId]
      return cal && cal.name ? cal.name : ''
    },
    hoursLabel () {
      const label = this.i18n[this.locale]['hours_in_school_year']
      if (!this.isEditingPool) {
        return label
      }
      return label + ' (' + this.i18n[this.locale]['pool_for'] + ' ' + this.poolCalendarName + ')'
    },
    subjectHours () {
      if (this.savedSubjectHours !== null) return this.savedSubjectHours
      return this.editSubject ? this.editSubject.hours : null
    },
    isPoolHoursChanged () {
      return this.isEditingPool && parseFloat(this.poolEntry.hours) !== parseFloat(this.hours)
    },
    showMasterCalendarName () {
      return this.editSubject && this.allCalendarsInHash[this.editSubject.calendar_id] && this.allCalendarsInHash[this.editSubject.calendar_id].name ? this.allCalendarsInHash[this.editSubject.calendar_id].name : ''
    },
    isShowSubjectsPools () {
      return this.isSchoolFromDenmark
    },
    schoolSettings () {
      return this.$store.state.schoolSettings || {}
    },
    isShowNorwegianPreparationDropdown () {
      return this.isArbeidstidsplanActivated && !this.isSchoolFromDenmark
    },
    isShowDanishPreparationFactorField () {
      return this.allowDanishPreparationFactorUi &&
        this.isSchoolFromDenmark &&
        this.schoolSettings &&
        this.schoolSettings.deactivatePools &&
        schoolHasDanishPreparationFactorConfigured(this.schoolSettings) &&
        isSubjectTypeWithDanishPreparationFactor(this.subjectType)
    },
    showDanishPreparationFactorBesideHours () {
      if (!this.isShowDanishPreparationFactorField) {
        return false
      }
      return !!this.editSubject || (!this.editSubject && !this.isEditSubjectAdvanced)
    },
    showDanishPreparationFactorInTitleRow () {
      return this.isShowDanishPreparationFactorField && !this.showDanishPreparationFactorBesideHours
    },
    isDanishPrepBinaryOnOwnRow () {
      return isDanishPreparationBinaryPrepTaskSubjectType(this.subjectType)
    },
    danishPreparationControlLabels () {
      const L = this.i18n[this.locale]
      return {
        forb_faktor: L.forb_faktor,
        forb_faktor_range_hint: L.forb_faktor_range_hint,
        prep_task_extra_toggle: L.prep_task_extra_toggle,
        prep_task_extra_toggle_hint: L.prep_task_extra_toggle_hint
      }
    },
    today () {
      return moment().format('YYYY-MM-DD')
    },
    activeSchoolYear () {
      return this.$store.state.localActivatedSchoolYear
    }
  },
  watch: {
    defaultDuration (val) {
      this.removeErrors('defaultDuration')
    },
    editSubject: {
      handler (val) {
        this.handleEditSubject()
        if (val && val._openTab) {
          this.activeTab = val._openTab
        }
      },
      immediate: true
    }
  },
  methods: {
    toggleDateFields () {
      this.isShowDateFields = !this.isShowDateFields
      if (this.isShowDateFields) {
        if (!this.fromDate && this.activeSchoolYear) {
          this.fromDate = this.activeSchoolYear.employee_time_start || null
        }
        if (!this.toDate && this.activeSchoolYear) {
          this.toDate = this.activeSchoolYear.employee_time_end || null
        }
      } else {
        this.fromDate = null
        this.toDate = null
      }
    },
    setFromDate (date) {
      const formatted = moment(date).format('YYYY-MM-DD')
      this.fromDate = formatted
      if (this.toDate && formatted > this.toDate) {
        this.toDate = formatted
      }
    },
    setToDate (date) {
      const formatted = moment(date).format('YYYY-MM-DD')
      this.toDate = formatted
      if (this.fromDate && formatted < this.fromDate) {
        this.fromDate = formatted
      }
    },
    hasDifferentiatedPools (subj) {
      if (!subj) return false

      let rows = []
      if (subj.subject_calendars && subj.subject_calendars.length) {
        rows = subj.subject_calendars
      } else if (subj.calendars_subjects && subj.calendars_subjects.length) {
        rows = subj.calendars_subjects
      } else {
        return false
      }

      for (let i = 0; i < rows.length; i++) {
        const sc = rows[i]
        if (!sc) continue
        const v = sc.hours !== undefined ? sc.hours : sc.value
        if (v !== null && v !== undefined) return true
      }
      return false
    },
    // Requests for the same subject share a websocket event id, so they must be sent one at a time
    applyHoursToCalendars (calendarIds, hours, done) {
      const subj = this.editSubject
      const finish = () => { if (done) done() }
      if (!subj || !Array.isArray(calendarIds)) return finish()
      const id = subj.id
      const schoolId = subj.school_id
      const schoolYearId = subj.school_year_id
      const queue = calendarIds.slice()
      const next = () => {
        if (queue.length === 0) return finish()
        docendo.setHoursForTeacherOnSubject(
          id,
          schoolId,
          schoolYearId,
          queue.shift(),
          hours,
          success => {
            if (success) {
              this.$store.commit('updateTeacherHoursOnSubject', success)
            }
            next()
          },
          failure => {
            console.log('failed to update pool hours', failure)
            next()
          }
        )
      }
      next()
    },
    userHasPermission (permission) {
      if (!this.user) {
        return false
      }
      if (this.isPermissionsActivatedForSchool) {
        return this.userPermissions.indexOf(permission) > -1 || this.user.admin
      }
      return true
    },
    toggleTaskOverview () {
      if (!this.canChangeTaskOverview) {
        return
      }
      this.taskOverview = !this.taskOverview
    },
    taskOverviewForCreate () {
      return this.canChangeTaskOverview ? this.taskOverview : false
    },
    taskOverviewForUpdate () {
      if (this.canChangeTaskOverview) {
        return this.taskOverview
      }
      return this.editSubject && typeof this.editSubject.task_overview !== 'undefined'
        ? this.editSubject.task_overview
        : null
    },
    selectAllOnFocus (evt) {
      setTimeout(_ => {
        evt.target.select()
      }, 0)
    },
    hasUnsavedChanges () {
      if (!this.editSubject) return false
      const cals = this.editSubject.calendar_ids || this.editSubject.cals || []
      const originalUsers = cals.filter(id => this.allCalendarsInHash[id] && this.allCalendarsInHash[id].type === 'user').slice().sort()
      const currentUsers = this.userCalendarIds.slice().sort()
      if (originalUsers.length !== currentUsers.length) return true
      for (let i = 0; i < originalUsers.length; i++) {
        if (originalUsers[i] !== currentUsers[i]) return true
      }
      return false
    },
    onClickCalendarHoursTab () {
      if (this.hasUnsavedChanges()) {
        if (window.confirm('Du har ugemte ændringer. Vil du gemme først?')) {
          this.$emit('saveAndShowTab', this.buildUpdatedSubject(), 'calendarHours')
          return
        }
      }
      this.activeTab = 'calendarHours'
    },
    buildUpdatedSubject () {
      let calIds = []
      this.userCalendarIds.forEach(calId => { calIds.push(calId) })
      this.roomCalendarIds.forEach(calId => { calIds.push(calId) })
      this.klassCalendarIds.forEach(calId => { calIds.push(calId) })
      return {
        id: this.editSubject.id,
        name: this.name,
        color: this.color,
        calendarId: this.calendarId,
        defaultDuration: this.defaultDuration,
        preparation: this.preparationPayloadForSave(),
        hours: this.isEditingPool ? this.subjectHours : this.hours,
        subjectType: this.subjectType,
        calendarIds: calIds,
        subjectCode: this.subjectCode,
        fromDate: this.fromDate || null,
        toDate: this.toDate || null,
        taskOverview: this.taskOverviewForUpdate()
      }
    },
    setSelectedUsers (users) {
      this.onTeacherSelectionUpdated(users)
    },
    setSelectedRooms (rooms) {
      this.onRoomSelectionUpdated(rooms)
    },
    setSelectedKlass (klasses) {
      this.onKlassSelectionUpdated(klasses)
    },
    addKlass (klass) {
      this.calendarId = klass.id
      this.removeErrors('calendarId')
    },
    removeKlass () {
      this.calendarId = null
    },
    setSubjectColor (val) {
      this.color = val
    },
    formValidation () {
      if (this.name.length < 1) {
        this.$set(this.formErrors, 'name', {
          da: 'Fag skal have et navn',
          en: 'Subjects must have a name',
          nb: 'Fag må ha et navn'
        })
      }
      if (this.hours < 0) {
        this.$set(this.formErrors, 'hours', {
          da: 'Skal være 0 eller højere',
          en: 'Must be zero or greater',
          nb: 'Må være 0 eller høyere'
        })
      }
      if (!this.isAValidDuration(this.defaultDuration)) {
        this.$set(this.formErrors, 'defaultDuration', {
          da: 'Må ikke være højere end 1440 minutter',
          en: 'Can not be greater than 1440 minutes',
          nb: 'Kan ikke være lengre enn 1440 minutter'
        })
      }
      if (!this.calendarId) {
        this.$set(this.formErrors, 'calendarId', {
          da: 'Skal vælges. Du kan bagefter kopiere til andre',
          en: 'Must choose a class. It can later be copied',
          nb: 'Må velges. Du kan deretter kopiere til andres'
        })
      }
    },
    removeErrors (title) {
      this.$delete(this.formErrors, title)
    },
    createSubject () {
      this.formValidation()
      if (Object.keys(this.formErrors).length > 0) {
        return
      }
      let newSubject = {
        name: this.name,
        color: this.color,
        calendarId: this.calendarId,
        defaultDuration: this.defaultDuration,
        hours: this.hours,
        subjectType: this.subjectType,
        calendarIds: [],
        subjectCode: this.subjectCode,
        preparation: this.preparationPayloadForSave(),
        fromDate: this.fromDate || null,
        toDate: this.toDate || null,
        taskOverview: this.taskOverviewForCreate()
      }
      this.$emit('createSubject', newSubject)
    },
    updateSubject () {
      this.formValidation()
      if (Object.keys(this.formErrors).length > 0) {
        return
      }
      const savedSubject = Object.assign({}, this.editSubject, {hours: this.subjectHours})
      if (!this.isEditingPool && shouldConfirmPoolsUpdate(savedSubject, this.hours)) {
        this.showPoolsConfirm = true
        return
      }
      if (this.shouldWaitForTeacherAssignBeforeUpdate()) {
        return
      }
      this.doUpdateSubject()
    },
    doUpdateSubject () {
      if (!this.isPoolHoursChanged) {
        this.emitUpdatedSubject()
        return
      }
      const subj = this.editSubject
      docendo.setHoursForTeacherOnSubject(
        subj.id,
        subj.school_id,
        subj.school_year_id,
        this.poolCalendarId,
        this.hours,
        success => {
          if (success) {
            this.$store.commit('updateTeacherHoursOnSubject', success)
          }
          this.emitUpdatedSubject()
        },
        failure => {
          console.log('failed to update pool hours', failure)
          eventbus.toast = { text: this.i18n[this.locale]['pool_save_failed'], success: false }
        }
      )
    },
    emitUpdatedSubject () {
      let calIds = []
      this.userCalendarIds.forEach(calId => {
        calIds.push(calId)
      })
      this.roomCalendarIds.forEach(calId => {
        calIds.push(calId)
      })
      this.klassCalendarIds.forEach(calId => {
        calIds.push(calId)
      })
      this.updatedSubject = {
        id: this.editSubject.id,
        name: this.name,
        color: this.color,
        calendarId: this.calendarId,
        defaultDuration: this.defaultDuration,
        preparation: this.preparationPayloadForSave(),
        hours: this.isEditingPool ? this.subjectHours : this.hours,
        subjectType: this.subjectType,
        calendarIds: calIds,
        subjectCode: this.subjectCode,
        fromDate: this.fromDate || null,
        toDate: this.toDate || null,
        taskOverview: this.taskOverviewForUpdate()
      }
      if (this.klassCalendarIds.length === 0) {
        this.$emit('updateSubject', this.updatedSubject)
      } else {
        this.checkIfAddedKlassAlreadyHasSubjectWithSameName()
      }
    },
    onPoolsConfirmUpdateAll (calendarIds) {
      this.showPoolsConfirm = false
      this.applyHoursToCalendars(calendarIds, this.hours, () => this.proceedAfterPoolsConfirm())
    },
    onPoolsConfirmSubjectOnly () {
      this.showPoolsConfirm = false
      this.proceedAfterPoolsConfirm()
    },
    proceedAfterPoolsConfirm () {
      if (this.shouldWaitForTeacherAssignBeforeUpdate()) {
        return
      }
      this.doUpdateSubject()
    },
    onPoolsConfirmClose () {
      this.showPoolsConfirm = false
    },
    onSubjectHoursSaved (hours) {
      this.savedSubjectHours = hours
      if (!this.isEditingPool) {
        this.hours = hours
      }
    },
    checkIfAddedKlassAlreadyHasSubjectWithSameName () {
      let newKlassesAddedOnSubject = []
      this.klassCalendarIds.forEach(calId => {
        if (this.editSubject.calendar_ids.indexOf(calId) < 0) {
          newKlassesAddedOnSubject.push(calId)
        }
      })
      if (newKlassesAddedOnSubject.length > 0) {
        newKlassesAddedOnSubject.forEach(calId => {
          // get all subjects for a klass
          const subjectIdsForKlass = this.subjectsInKlassHash[calId]
          const klassSubjects = subjectIdsForKlass.map(subjectId => {
            if (this.subjectsInHash[subjectId]) {
              return this.subjectsInHash[subjectId]
            }
          })
          klassSubjects.forEach(subject => {
            if (subject.name === this.name) {
              this.subjectsWithSameName.push(subject)
            }
          })
        })
        if (this.subjectsWithSameName.length === 0) {
          this.$emit('updateSubject', this.updatedSubject)
        }
      } else {
        this.$emit('updateSubject', this.updatedSubject)
      }
    },
    deleteExistingSubjects () {
      this.subjectsWithSameName.forEach(subject => {
        this.$emit('deleteSubject', subject)
      })
      this.$emit('updateSubject', this.updatedSubject)
    },
    keepExistingSubjects () {
      this.subjectsWithSameName = []
      this.$emit('updateSubject', this.updatedSubject)
    },
    closeModalSubjectsWithSameName () {
      this.subjectsWithSameName = []
    },
    deleteEditSubject () {
      this.$emit('deleteSubject', this.editSubject)
    },
    clearForm () {
      this.formErrors = []
      this.name = ''
      this.calendarIds = []
      this.hours = 0
      this.color = '#A0A5EF'
      this.setDefaultDuration()
      this.subjectCode = ''
      this.subjectsWithSameName = []
      this.updatedSubject = null
      this.preparation = ''
      this.fromDate = null
      this.toDate = null
      this.isShowDateFields = false
      this.taskOverview = false
    },
    preparationPayloadForSave () {
      return preparationPayloadForSave(this.preparation, this.isShowDanishPreparationFactorField, this.subjectType)
    },
    preparationFromApiForForm (prep) {
      return preparationFromApiForForm(prep, this.isShowDanishPreparationFactorField, this.subjectType)
    },
    close () {
      this.clearForm()
      this.$emit('close')
    },
    selectSubjectType (subjectType) {
      this.subjectType = subjectType
    },
    saveUpdatedDuration () {
      this.setDefaultDuration()
      this.$refs.input_default_duration.focus()
      this.$refs.input_default_duration.select()
    },
    setDefaultDuration () {
      if (this.activeSchool && this.activeSchool.subject_default_duration) {
        this.defaultDuration = this.activeSchool.subject_default_duration
      } else {
        this.defaultDuration = 45
      }
    },
    isAValidDuration (duration) {
      return /^-?[\d.]+(?:e-?\d+)?$/.test(duration) && duration > 0 && duration < 1440
    },
    handleEditSubject () {
      this.savedSubjectHours = null
      if (this.editSubject) {
        this.name = this.editSubject.name
        this.color = this.editSubject.color
        this.hours = this.isEditingPool ? parseFloat(this.poolEntry.hours) : this.editSubject.hours
        this.defaultDuration = this.editSubject.default_duration
        this.subjectType = this.editSubject.subject_type
        this.calendarId = this.editSubject.calendar_id
        this.subjectCode = this.editSubject.subject_code

        if (this.editSubject.preparation != null) {
          this.preparation = this.preparationFromApiForForm(this.editSubject.preparation)
        } else {
          this.preparation = null
        }
        this.fromDate = this.editSubject.from_date || null
        this.toDate = this.editSubject.to_date || null
        this.isShowDateFields = !!(this.fromDate || this.toDate)
        this.taskOverview = typeof this.editSubject.task_overview !== 'undefined' ? this.editSubject.task_overview : false

        const cals = this.editSubject.calendar_ids ? this.editSubject.calendar_ids : this.editSubject.cals
        if (cals) {
          cals.forEach(calid => {
            if (this.allCalendarsInHash[calid] && this.allCalendarsInHash[calid].type === 'user') {
              this.userCalendarIds.push(calid)
            }
            if (this.allCalendarsInHash[calid] && this.allCalendarsInHash[calid].type === 'room') {
              this.roomCalendarIds.push(calid)
            }
            if (this.allCalendarsInHash[calid] && this.allCalendarsInHash[calid].type === 'klass' && calid !== this.calendarId) {
              this.isPossibleToAddKlasses = true
              this.klassCalendarIds.push(calid)
            }
          })
        }
      } else {
        this.setDefaultDuration()
      }
    }
  },
  beforeMount () {
    if (this.propTab) {
      this.activeTab = this.propTab
    }
    if (this.isActivities) {
      this.subjectType = 'activity'
    }
  },
  mounted () {
    if (this.propCalendarId) {
      this.calendarId = this.propCalendarId
    }
    if (this.editSubject) {
      if (this.activeTab === 'edit') {
        setTimeout(_ => {
          if (this.$refs.inputLessonCount) this.$refs.inputLessonCount.focus()
        }, 500)
      }
      return
    }
    setTimeout(_ => {
      if (this.$refs.name) this.$refs.name.focus()
    }, 150)
  }
}
</script>

<style scoped>
@import "../../assets/css/variables.css";

.add-lesson__header {
  position: relative;
  transition: background-color 0.4s;
  padding: 30px 20px;
  color: #ffffff;
  border-radius: 4px 4px 0 0;
}

.add-lesson__header > h2 {
  color: white;
  margin-top: 0;
}

.add-lesson__form {
  position: relative;
  padding: 1rem 2rem;
  overflow-y: scroll;
  max-height: calc(100vh - 10vh - 120px); /* 120px is modal header; 10vh is modal from vtop */
}

.add-lesson__header__tabs {
  position: absolute;
  z-index: 10;
  bottom: 0;
  right: 0;
  display: flex;
}

.add-lesson__header__task-overview {
  position: absolute;
  z-index: 10;
  top: 10px;
  right: 44px;
  display: flex;
  align-items: center;
  gap: 8px;
  cursor: pointer;
  user-select: none;
}

.add-lesson__header__task-overview label {
  color: #ffffff;
  font-size: 0.75rem;
  letter-spacing: .05rem;
  text-transform: uppercase;
  font-weight: 700;
  cursor: pointer;
  margin: 0;
}

.add-lesson__header__tabs__tab {
  margin-right: 15px;
  cursor: pointer;
  padding: 5px;
  border-radius: 4px 4px 0 0;
}

.add-lesson__header__tabs__tab.tab--is-active {
  background-color: #f6f9fc;
  color: #3e4450;
  font-weight: bold;
}

.add-lesson__form .input-group {
  padding-bottom: 1rem;
}

.add-lesson__calendars__calendar {
  margin-left: 0.5rem;
  position: relative;
}

.add-lesson__calendars__calendar:not(:last-child):after {
  content: ',';
  position: absolute;
  bottom: 0;
  right: -5px;
}

.input-group__submit {
  display: flex;
  justify-content: space-between;
}

.add-lesson__form .add-lesson__footer {
  position: sticky;
  bottom: -1rem;
  z-index: 5;
  margin: 0 -2rem -1rem;
  padding: 0.75rem 2rem;
  background-color: #f6f9fc;
  border-top: 1px solid #e3e8ee;
}

.add-lesson__form .add-lesson__footer .input-group {
  margin: 0;
  padding: 0;
}

.input-group__flex {
  display: flex;
  flex: 1;
  width: 100%;
  gap: 1rem;
}

.input-group__flex > div {
  flex: 1;
}

.input-group__flex > .flex-2 {
  flex: 2;
}

.input-group__flex > .flex-1 {
  flex: 1;
  margin-left: 1rem;
}

.input-group input {
  background-color: #ffffff;
}

.lesson-count__edit {
  display: flex;
  align-items: center;
  max-width: 160px;
}

.lesson-count__edit input[type="number"] {
  margin: 0 0.25rem;
  text-align: center;
}

.button__delete-subject {
  color: var(--danger);
}

.expand-enter-active, .expand-leave-active {
  transition: transform 0.25s ease-in-out, opacity 0.25s ease-in-out;
}

.expand-enter, .expand-leave-to {
  transform: translateX(-0.5rem);
  opacity: 0;
}

.input-group label {
  letter-spacing: .075rem;
  text-transform: uppercase;
  font-size: 0.85rem;
  line-height: 1rem;
  font-weight: 700;
  display: block;
  color: #a2a3a6;
  margin-bottom: 6px;
}

.advanced-settings__link {
  cursor: pointer;
}

.advanced-settings__link:hover {
  text-decoration: underline;
}

.bttn.bttn--disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.do-input.input--disabled {
  background-color: #f1f3f6;
  color: #a2a3a6;
  cursor: not-allowed;
}

.bttn.bttn--disabled:active {
  background: #b34e4d;
}

.input-group--flex {
  display: flex;
  align-items: center;
}

.input-group--flex input {

  width: 100%;
}

.input-group--flex > div:not(.input-group--flex__setting-icon) {
  width: 100%;
  flex: 1;
}

.input-group--flex__setting-icon {
  padding-left: 10px;
}

.text-link {
  color: var(--blue) !important;
  text-decoration: underline;
  margin-bottom: 1rem;
  cursor: pointer;
}

.subjects-requested-by {
  margin-bottom: 0.5rem;
  font-size: 11px;
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 1rem;
  padding: 0.25rem 0.5rem;
  border-radius: 4px;
  text-transform: capitalize;
  letter-spacing: normal;
  font-style: italic;
  border: 1px solid #cec9fd;
  color: #6772e5 !important;
  background-color: #eeebff;
}

.subjects-requested-by .do-label {
  margin-bottom: 0;
  font-style: normal;
}

.input-group__submit__right {
  display: flex;
  align-items: center;
  gap: 1rem;
}

.input-group__subject-codes .d-input__select {
  max-width: 100%;
}

.date-field {
  position: relative;
  display: flex;
  align-items: center;
}

.date-toggle {
  display: flex;
  align-items: center;
  user-select: none;
  cursor: pointer;
}

.date-toggle__inner {
  display: flex;
  align-items: center;
  cursor: pointer;
}

.date-toggle__inner label {
  margin-bottom: 0;
  margin-left: 10px;
  cursor: pointer;
}

.date-toggle__inner label:hover {
  color: var(--dark-blue);
}

</style>



// WEBPACK FOOTER //
// SubjectForm.vue?6ca6640c